<template>
  <div
    v-if="noCommentsMessage"
    class="NoParticipationComments"
    v-text="$t(noCommentsMessage)"
  />
  <div v-else>
    <header class="ParticipationHeader">
      <Feedback
        v-if="!isParticipationEnabled"
        class="mb-3"
        :dismissible="false"
        :feedback="{ variant: 'warning' }"
      >
        <strong>{{ $t('components.sidebar.ParticipationComments.demoMsg') }}</strong> <br>
        <span>{{ $t('components.sidebar.ParticipationComments.interest') }}
          <MailtoSupport />
        </span>
      </Feedback>
      <Feedback
        v-if="isCommentsDisabledForStatus"
        class="mb-3"
        :dismissible="false"
        :feedback="{ variant: 'warning' }"
      >
        <span v-text="$t('components.sidebar.ParticipationComments.commentsDisabledForStatus')" />
      </Feedback>
      <Feedback
        v-if="notASuggestionMessage"
        class="mb-3"
        :dismissible="false"
        :feedback="{ variant: 'warning' }"
      >
        <span v-text="$t(notASuggestionMessage)" />
      </Feedback>
      <div class="ParticipatieSentiment">
        <div class="ParticipatieSentimentText">
          <div
            v-text="`${negativeCommentsCount} ${negativeCommentsCount === 1 ? $t('components.sidebar.ParticipationComments.negative') : $t('components.sidebar.ParticipationComments.negatives')}`"
          />
          <div
            v-text="`${positiveCommentsCount}  ${positiveCommentsCount === 1 ? $t('components.sidebar.ParticipationComments.positive') : $t('components.sidebar.ParticipationComments.positives')}`"
          />
        </div>
        <div
          v-if="!commentsCountWithSentiment"
          class="ParticipatieSentimentIndicator NoComments"
        />
        <div
          v-else
          class="ParticipatieSentimentIndicator"
        >
          <span
            v-if="negativeCommentsCount"
            class="pl-2"
            v-text="`${Math.round(negativeCommentsRelative * 100)}%`"
          />
          <div
            v-if="positiveCommentsCount"
            class="Positive pr-2"
            :class="{ 'AllPositive': !negativeCommentsCount }"
            :style="`width: ${positiveCommentsCount / commentsCountWithSentiment * 100}%`"
            v-text="`${Math.round(positiveCommentsRelative * 100)}%`"
          />
        </div>
      </div>

      <div
        class="ParticipationCommentsCount"
        v-text="`${commentsCount} ${commentsCount === 1 ? $t('components.sidebar.TheComments.comment') : $t('components.sidebar.TheComments.comments')}`"
      />
    </header>
    <ul class="ParticipationComments">
      <ParticipationCommentsItem
        v-for="comment in participationComments"
        :key="comment._id"
        :comment="comment"
        :is-demo="!isParticipationEnabled"
        @update-status="status => handleUpdateStatus({ comment, status })"
      />
    </ul>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { PARTICIPATION_SENTIMENT } from '@/../shared/valueholders/participation-sentiment'
import fakeParticipationComments from '@/data/fakeParticipationComments'
import Feedback from '@/components/form/Feedback'
import MailtoSupport from '@/components/common/MailtoSupport'
import ParticipationCommentsItem from './ParticipationCommentsItem'
import { updateParticipationComment, getParticipationComments } from '@/../shared/API/participation-comments'

export default {
  name: 'ParticipationComments',
  components: {
    Feedback,
    MailtoSupport,
    ParticipationCommentsItem,
  },
  props: {
    chargingpoint: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      participationComments: fakeParticipationComments,
    }
  },
  computed: {
    ...mapGetters('config', [
      'isParticipationEnabled',
      'participationConfigs',
    ]),
    ...mapGetters('access', [
      'getActiveMunicipality',
    ]),
    isCommentsDisabledForStatus() {
      const status = this.chargingpoint.properties.status
      const statusCommentConfig = this.participationConfigs[0]?.statusCommentConfig ?? []
      const entry = statusCommentConfig.find(c => c.value === status)
      return entry?.commentsEnabled !== true
    },
    notASuggestionMessage() {
      const notASuggestionMessages = {
        'rejected': 'components.sidebar.ParticipationComments.notASuggestionMessages.rejected',
        'in-progress': 'components.sidebar.ParticipationComments.notASuggestionMessages.in-progress',
        'realized': 'components.sidebar.ParticipationComments.notASuggestionMessages.realized',
        'realized-semipublic': 'components.sidebar.ParticipationComments.notASuggestionMessages.realized-semipublic',
        'fastcharger-realized-semipublic': 'components.sidebar.ParticipationComments.notASuggestionMessages.fastcharger-realized-semipublic',
        'fastcharger-rejected': 'components.sidebar.ParticipationComments.notASuggestionMessages.fastcharger-rejected',
        'fastcharger-realized': 'components.sidebar.ParticipationComments.notASuggestionMessages.fastcharger-realized',
        'charging-hub-rejected': 'components.sidebar.ParticipationComments.notASuggestionMessages.charging-hub-rejected',
      }

      const status = this.chargingpoint.properties.status
      const notASuggestionMessage = notASuggestionMessages[status]

      if (notASuggestionMessage) {
        return notASuggestionMessage
      }

      return false
    },
    noCommentsMessage() {
      if (this.isLoading) {
        return this.$i18n.t('components.sidebar.ParticipationComments.loading')
      }

      if (this.participationComments.length) {
        return false
      }

      const statusMessages = {
        'alert': 'components.sidebar.ParticipationComments.statusMessages.alert',
        'rejected': 'components.sidebar.ParticipationComments.statusMessages.rejected',
        'in-progress': 'components.sidebar.ParticipationComments.statusMessages.in-progress',
        'realized': 'components.sidebar.ParticipationComments.statusMessages.realized',
        'realized-private': 'components.sidebar.ParticipationComments.statusMessages.realized-private',
        'fastcharger-realized-private': 'components.sidebar.ParticipationComments.statusMessages.fastcharger-realized-private',
        'fastcharger-rejected': 'components.sidebar.ParticipationComments.statusMessages.fastcharger-rejected',
        'fastcharger-realized': 'components.sidebar.ParticipationComments.statusMessages.fastcharger-realized',
        'charging-hub-rejected': 'components.sidebar.ParticipationComments.statusMessages.charging-hub-rejected',
      }

      const status = this.chargingpoint.properties.status
      const statusMessage = statusMessages[status]

      if (statusMessage) {
        return statusMessage
      }

      return this.$i18n.t('components.sidebar.ParticipationComments.noComments')
    },
    commentsCount() {
      return this.participationComments.length
    },
    positiveCommentsCount() {
      return this.participationComments.filter((data) => data.sentiment === PARTICIPATION_SENTIMENT.POSITIVE).length
    },
    negativeCommentsCount() {
      return this.participationComments.filter((data) => data.sentiment === PARTICIPATION_SENTIMENT.NEGATIVE).length
    },
    commentsCountWithSentiment() {
      return this.positiveCommentsCount + this.negativeCommentsCount
    },
    positiveCommentsRelative() {
      return this.positiveCommentsCount / this.commentsCountWithSentiment
    },
    negativeCommentsRelative() {
      return this.negativeCommentsCount / this.commentsCountWithSentiment
    },
  },
  watch: {
    chargingpoint: {
      immediate: true,
      handler() {
        if (this.isParticipationEnabled) {
          this.fetchParticipationComments()
        }
      },
    },
  },
  methods: {
    ...mapActions('planmode', [
      'addOrUpdateChargingPoint',
      'deleteComment',
    ]),
    async fetchParticipationComments() {
      try {
        this.isLoading = true

        const comments = await getParticipationComments(this.chargingpoint.uuid)

        this.participationComments = (comments || fakeParticipationComments).sort((a, b) => b.created_at - a.created_at)
        this.participationComments.forEach(comment => this.$store.dispatch('planmode/addOrUpdateParticipationComment', ({ comment })))
      } catch (err) {
        console.error('Error fetching participation comments:', err)
        this.participationComments = []
      } finally {
        this.isLoading = false
      }
    },
    toLocalDate({ timestamp }) {
      const format = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }

      let date = new Date(timestamp)

      return date.toLocaleString([], format)
    },
    async handleUpdateStatus({ comment, status }) {
      try {
        const updatedComment = await updateParticipationComment({
          commentUUID: comment.uuid,
          status,
          user: {
            name: this.currentUserName,
            id: this.currentUserId,
          },
        })

        const index = this.participationComments.findIndex(c => c.uuid === updatedComment.uuid)
        if (index !== -1) {
          this.participationComments.splice(index, 1, updatedComment)
        } else {
          this.participationComments.push(updatedComment)
        }

        this.$store.dispatch('planmode/addOrUpdateParticipationComment', { comment: updatedComment, where: 'in action' })
      } catch (err) {
        console.error('Error updating participation comment:', err)
      }
    },

  },
}
</script>

<style lang="scss">
.NoParticipationComments {
  padding: 2rem 0;
  text-align: center;
  color: gray;
}

.ParticipationHeader {
  border-bottom: 1px solid var(--border-color);

  .ParticipationCommentsCount {
    padding: 0.5rem 0;
    margin-right: 0.5rem;
    font-size: 0.9rem;
    text-align: right;
  }

  .ParticipatieSentiment {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--border-color);

    .ParticipatieSentimentText {
      display: flex;
      justify-content: space-between;
      color: dimgray;
      font-size: 0.9rem;
    }

    .ParticipatieSentimentIndicator {
      display: flex;
      justify-content: space-between;
      height: 1.4rem;
      width: 100%;
      background-color: #dc3545;
      border-radius: 0.25rem;
      color: white;
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1.4rem;

      &.NoComments {
        background-color: lightgray;
      }

      .Positive {
        height: 100%;
        background-color: #5d8c30;
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;
        text-align: right;

        &.AllPositive {
          border-top-left-radius: 0.25rem;
          border-bottom-left-radius: 0.25rem;
        }
      }
    }
  }
}

.ParticipationComments {
  padding-left: 0;
  list-style: none;
}
</style>
